import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';

import {
  FeatureNames,
  RenameHeatingCircuitCommands,
  RenameHeatingCircuitProperties,
  IsStringValueProperty,
  Feature,
  OperatingProgramsReducedEnergySavingProperties,
  HeatingConfigurationRegulationProperties,
  DcSensorValueProperties,
  CirculationPumpStatusProperties,
  FeaturePropertyKey,
} from '@vi-iot/client';

import { InstallationFeaturesStore } from '@store';

import { EditHeatingCircuitNameModalComponent } from '../../heating-circuit-features-panel/edit-heating-circuit-name-modal/edit-heating-circuit-name-modal.component';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { PillType } from '../../../../shared/components/pill/pill.model';

@Component({
  selector: 'app-circuit-heating-zone-features-panel',
  templateUrl: './circuit-heating-zone-features-panel.component.html',
  styleUrls: ['./circuit-heating-zone-features-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CircuitHeatingZoneFeaturesPanelComponent implements OnInit {
  readonly pillType = PillType;
  readonly featurePropertyKey = FeaturePropertyKey;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;

  @Input() deviceSelectId: string;
  @Input() deviceId: string;
  @Input() circuitId: string;
  @Input() header: string;

  heatingZoneNameFeature$: Observable<
    Feature<RenameHeatingCircuitProperties, RenameHeatingCircuitCommands>
  >;
  operatingProgramsActive$: Observable<Feature<IsStringValueProperty, never>>;
  circuitReducedEnergySaving$: Observable<
    Feature<OperatingProgramsReducedEnergySavingProperties, never>
  >;
  circuitTemperature$: Observable<Feature<IsStringValueProperty, never>>;
  circuitSensorsTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  circuitConfigurationRegulation$: Observable<
    Feature<HeatingConfigurationRegulationProperties, never>
  >;
  operatingProgramName$: Observable<string>;

  pumpStatus$: Observable<Feature<CirculationPumpStatusProperties, never>>;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.heatingZoneNameFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitName(this.circuitId),
      this.deviceId
    );
    this.operatingProgramsActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitActiveProgram(this.circuitId),
      this.deviceId
    );
    this.circuitReducedEnergySaving$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitReducedEnergySaving(this.circuitId),
      this.deviceId
    );
    this.circuitTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitTemperature(this.circuitId),
      this.deviceId
    );
    this.circuitSensorsTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSupplyTemperature(this.circuitId),
      this.deviceId
    );
    this.circuitConfigurationRegulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitRegulation(this.circuitId),
      this.deviceId
    );
    this.operatingProgramName$ = this.installationFeaturesStore.getCircuitHeatingZoneOperatingProgramName(
      this.circuitId
    );
    this.pumpStatus$ = this.circuitId
      ? this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.circuitCirculationPump(this.circuitId),
          this.deviceId
        )
      : of(null);
  }

  openRenameHeatingZoneModal(): void {
    this.dialog.open(EditHeatingCircuitNameModalComponent, {
      data: {
        name: this.header,
        circuitId: this.circuitId,
      },
    });
  }
}
